import React, { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { ChevronUp, ChevronDown, Activity, Target, Shield, TrendingUp, AlertTriangle, History, BarChart3, ShieldCheck, ShieldAlert, Terminal, Crosshair } from 'lucide-react';
import clsx from 'clsx';

export const MissionScoreboard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const stats = useGameStore(state => state.stats);
  const sectors = useGameStore(state => state.sectors);
  const turn = useGameStore(state => state.turn);
  const maxTurns = useGameStore(state => state.maxTurns);
  const gameStatus = useGameStore(state => state.gameStatus);
  const currentScenario = useGameStore(state => state.currentScenario);
  const selectSector = useGameStore(state => state.selectSector);

  // Auto-cycle targets every 5 seconds
  useEffect(() => {
    if (!currentScenario?.targetSectorIds?.length) return;
    const interval = setInterval(() => {
      setCurrentTargetIndex(prev => (prev + 1) % (currentScenario.targetSectorIds?.length || 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [currentScenario]);

  if (gameStatus !== 'playing') return null;

  // Calculate Metrics
  const securedSectors = sectors.filter(s => s.trustScore >= 8).length;
  const contestedSectors = sectors.filter(s => s.trustScore >= 3 && s.trustScore < 8).length;
  const hostileSectors = sectors.filter(s => s.trustScore < 3).length;
  
  const averageTrust = (sectors.reduce((acc, s) => acc + s.trustScore, 0) / sectors.length).toFixed(1);
  const passingScore = currentScenario?.passingScore || 8.5;
  const activeThreats = sectors.reduce((acc, s) => acc + s.risks.filter(r => r.active).length, 0);

  // Target Sector Logic
  const targetSectorIds = currentScenario?.targetSectorIds || [];
  const currentTargetId = targetSectorIds[currentTargetIndex];
  const targetSector = sectors.find(s => s.id === currentTargetId);

  // Strategic Advice Generator
  const getStrategicAdvice = () => {
    if (activeThreats > 3) return { text: "THREAT CRITICAL. PRIORITIZE JAMMING.", type: 'warning' };
    if (securedSectors < 3 && turn > 3) return { text: "EXPANSION SLOW. INITIATE BROADCAST.", type: 'warning' };
    if (stats.totalApSpent > 0 && stats.successfulActions / stats.actionsTaken < 0.5) return { text: "EFFICIENCY LOW. USE LISTEN FIRST.", type: 'info' };
    return { text: "OPERATIONS NOMINAL. CONTINUE MISSION.", type: 'success' };
  };

  const advice = getStrategicAdvice();

  return (
    <div className={clsx(
      "fixed top-24 right-4 z-30 transition-all duration-500 ease-out flex flex-col items-end pointer-events-auto",
      isExpanded ? "w-80" : "w-auto"
    )}>
      {/* Toggle Button */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="glass-panel p-2 rounded-t-lg flex items-center gap-2 hover:bg-slate-800/50 transition-colors shadow-lg mb-1"
      >
        <Activity className="w-4 h-4 text-ops-blue" />
        <span className="font-bold text-xs tracking-widest uppercase font-mono">SITREP</span>
        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>

      {/* Main Content */}
      {isExpanded && (
        <div className="glass-panel rounded-b-lg p-5 shadow-2xl w-full animate-slide-in relative overflow-hidden">
          
          {/* Decorative Grid Background */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-20 pointer-events-none"></div>

          {/* Header Stats */}
          <div className="grid grid-cols-3 gap-4 mb-5 border-b border-slate-700/50 pb-4 relative z-10">
            <div className="text-center">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 font-mono">Turn</div>
              <div className="text-xl font-bold text-white font-mono">{turn}/{maxTurns}</div>
            </div>
            <div className="text-center border-l border-slate-700/50">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 font-mono">Trust</div>
              <div className={clsx("text-xl font-bold font-mono", Number(averageTrust) > 5 ? "text-ops-green" : "text-ops-red")}>
                {averageTrust}
              </div>
              <div className="text-[9px] text-slate-500 font-mono mt-1">Target: {passingScore}</div>
            </div>
            <div className="text-center border-l border-slate-700/50">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 font-mono">Threats</div>
              <div className={clsx("text-xl font-bold font-mono", activeThreats > 0 ? "text-ops-red animate-pulse" : "text-slate-500")}>
                {activeThreats}
              </div>
            </div>
          </div>

          {/* Target Sectors Display */}
          {targetSector && (
            <div className="mb-5 relative z-10 bg-slate-800/50 rounded border border-slate-700 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest flex items-center gap-2 font-mono">
                  <Crosshair className="w-3 h-3 animate-spin-slow" /> 
                  Target Sector ({currentTargetIndex + 1}/{targetSectorIds.length})
                </h4>
                <div className="flex gap-1">
                  {targetSectorIds.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={clsx("w-1.5 h-1.5 rounded-full cursor-pointer transition-colors", idx === currentTargetIndex ? "bg-blue-400" : "bg-slate-600")}
                      onClick={() => setCurrentTargetIndex(idx)}
                    />
                  ))}
                </div>
              </div>
              
              <div 
                className="flex items-center justify-between cursor-pointer hover:bg-slate-700/50 p-1 rounded transition-colors"
                onClick={() => selectSector(targetSector.id)}
              >
                <div className="flex items-center gap-2">
                  <div className={clsx("w-2 h-2 rounded-full", targetSector.trustScore >= 8 ? "bg-green-500" : "bg-red-500")} />
                  <span className="text-sm font-bold text-slate-200">{targetSector.name}</span>
                </div>
                <span className="font-mono text-xs text-slate-400">Trust: {targetSector.trustScore}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-1 italic">
                {targetSector.description.substring(0, 50)}...
              </div>
            </div>
          )}

          {/* Sector Breakdown */}
          <div className="space-y-3 mb-5 relative z-10">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 font-mono">
              <Target className="w-3 h-3" /> Sector Control
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300 flex items-center gap-2">
                  <ShieldCheck className="w-3 h-3 text-ops-blue" /> Secured
                </span>
                <span className="font-mono font-bold text-ops-blue">{securedSectors}</span>
              </div>
              <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                <div className="bg-ops-blue h-full transition-all duration-500" style={{ width: `${(securedSectors / sectors.length) * 100}%` }} />
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300 flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-ops-amber" /> Contested
                </span>
                <span className="font-mono font-bold text-ops-amber">{contestedSectors}</span>
              </div>
              <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                <div className="bg-ops-amber h-full transition-all duration-500" style={{ width: `${(contestedSectors / sectors.length) * 100}%` }} />
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-300 flex items-center gap-2">
                  <ShieldAlert className="w-3 h-3 text-ops-red" /> Hostile
                </span>
                <span className="font-mono font-bold text-ops-red">{hostileSectors}</span>
              </div>
              <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                <div className="bg-ops-red h-full transition-all duration-500" style={{ width: `${(hostileSectors / sectors.length) * 100}%` }} />
              </div>
            </div>
          </div>

          {/* Tactical Advice */}
          <div className={clsx(
            "p-3 rounded-lg border text-xs font-mono relative overflow-hidden",
            advice.type === 'warning' ? "bg-ops-red/10 border-ops-red/30 text-ops-red" :
            advice.type === 'info' ? "bg-ops-blue/10 border-ops-blue/30 text-ops-blue" :
            "bg-ops-green/10 border-ops-green/30 text-ops-green"
          )}>
            <div className="flex items-start gap-2 relative z-10">
              <Terminal className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase tracking-wider mb-1 opacity-70">Tactical AI</div>
                <div className="leading-relaxed">{advice.text}</div>
              </div>
            </div>
            {/* Scanline for AI box */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent animate-scanline opacity-30 pointer-events-none"></div>
          </div>

        </div>
      )}
    </div>
  );
};
